"use client";

import * as React from "react";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  CheckIcon,
  ChevronRightIcon,
  DotIcon,
} from "lucide-react";

import { PortalWrapper } from "../prim.portal-wrapper";
import { Separator } from "../ui.separator";
import { cn } from "../util.cn";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = (
  props: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Portal>,
) => {
  const { children, ...portalProps } = props;
  return (
    <DropdownMenuPrimitive.Portal {...portalProps}>
      <PortalWrapper>
        {children}
      </PortalWrapper>
    </DropdownMenuPrimitive.Portal>
  );
};

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  {
    inset?: boolean;
  } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ children, className, inset, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cn(
      `
        flex cursor-default select-none items-center
        rounded-3 px-2 py-1.5 text-2 outline-none
        data-[state=open]:bg-panel
        focus:bg-panel
      `,
      inset && "pl-8",
      className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </DropdownMenuPrimitive.SubTrigger>
  );
});
DropdownMenuSubTrigger.displayName
  = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.SubContent
      className={cn(
      `
        z-50 min-w-32 overflow-hidden rounded-3 bg-panel p-1
        text-gray-11 shadow-3
        data-[side=bottom]:slide-in-from-top-2
        data-[side=left]:slide-in-from-right-2
        data-[side=right]:slide-in-from-left-2
        data-[side=top]:slide-in-from-bottom-2
        data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0
        data-[state=closed]:zoom-out-95
        data-[state=open]:animate-in
        data-[state=open]:fade-in-0
        data-[state=open]:zoom-in-95
      `,
      className,
      )}
      ref={ref}
      {...props}
    />
  );
});
DropdownMenuSubContent.displayName
  = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({
  className,
  // align with radix popover defaults
  // https://github.com/radix-ui/themes/blob/main/packages/radix-ui-themes/src/components/popover.tsx
  collisionPadding = 10,
  sideOffset = 8,
  ...props
}, ref) => {
  return (
    <DropdownMenuPortal>
      <DropdownMenuPrimitive.Content
        className={cn(
        `
          z-50 min-w-32 overflow-hidden rounded-3 bg-panel
          p-1 text-gray-11 shadow-3
        `,
        `
          data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2
          data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95
          data-[state=open]:animate-in
          data-[state=open]:fade-in-0
          data-[state=open]:zoom-in-95
        `,
        className,
        )}
        collisionPadding={collisionPadding}
        ref={ref}
        sideOffset={sideOffset}
        {...props}
      />
    </DropdownMenuPortal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  {
    inset?: boolean;
  } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, inset, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(
      `
        relative flex cursor-default select-none
        items-center gap-2 rounded-2 px-2 py-1.5 text-2
        outline-none transition-colors
        [&>svg]:size-4 [&>svg]:shrink-0
        data-[disabled]:pointer-events-none
        data-[disabled]:opacity-50
        focus:bg-accent-3 focus:text-accent-11
      `,
      inset && "pl-8",
      className,
      )}
      ref={ref}
      {...props}
    />
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ checked, children, className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(
      `
        relative flex cursor-default select-none
        items-center rounded-2 py-1.5 pl-8 pr-2 text-2
        outline-none transition-colors
        data-[disabled]:pointer-events-none
        data-[disabled]:opacity-50
        focus:bg-accent-3 focus:text-accent-11
      `,
      className,
      )}
      ref={ref}
      {...props}
    >
      <span className={`
        absolute left-2 flex size-3.5 items-center
        justify-center
      `}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
DropdownMenuCheckboxItem.displayName
  = DropdownMenuPrimitive.CheckboxItem.displayName;

interface DropdownMenuRadioItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> {
  checkedIndicator?: "check" | "dot";
}
const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(({ checkedIndicator = "dot", children, className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(
      `
        relative flex cursor-default select-none
        items-center rounded-2 py-1.5 pl-8 pr-2 text-2
        outline-none transition-colors
        data-[disabled]:pointer-events-none
        data-[disabled]:opacity-50
        focus:bg-accent-3 focus:text-accent-11
      `,
      className,
      )}
      ref={ref}
      {...props}
    >
      <span className={`
        absolute left-2 flex size-3.5 items-center
        justify-center
      `}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          {checkedIndicator === "check" && (
            <CheckIcon className="size-4" />
          )}
          {checkedIndicator === "dot" && (
            <DotIcon className="size-4" />
          )}
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  {
    inset?: boolean;
  } & React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, inset, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Label
      className={cn(
        "px-2 py-1.5 text-2 font-semibold",
        inset && "pl-8",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentPropsWithoutRef<typeof Separator>
>((props, ref) => {
  const { size = "4", ...separatorProps } = props;
  return (
    <Separator
      my="1"
      ref={ref}
      {...separatorProps}
      size={size}
    />
  );
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(`
        ml-auto text-1 tracking-7 opacity-60
      `, className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu as Root,
  DropdownMenuCheckboxItem as CheckboxItem,
  DropdownMenuContent as Content,
  DropdownMenuGroup as Group,
  DropdownMenuItem as Item,
  DropdownMenuLabel as Label,
  DropdownMenuPortal as Portal,
  DropdownMenuRadioGroup as RadioGroup,
  DropdownMenuRadioItem as RadioItem,
  DropdownMenuSeparator as Separator,
  DropdownMenuShortcut as Shortcut,
  DropdownMenuSub as Sub,
  DropdownMenuSubContent as SubContent,
  DropdownMenuSubTrigger as SubTrigger,
  DropdownMenuTrigger as Trigger,
};
